import React, { useCallback, useLayoutEffect, useState } from "react";
import { timeDay } from "d3";
import { animated } from "@react-spring/web";
import { useSpring, config } from "@react-spring/core";

import "./FilterPanel.scss";
import { Button, FilterGroup, Slider, Search } from "..";
import {
  // CHART_DIMENSIONS,
  formatThousands,
  formatTimeDayMonthYear,
  LAYOUT,
} from "../../utils";
import { useStore } from "../../store";
import FilterDate from "./FilterDate";

const updateCSSVariables = (width, panelX) => {
  document
    .getElementById("root")
    .style.setProperty(
      "--panel-x-offset",
      `${Math.abs(Number.parseInt(panelX))}px`
    );

  const cardStackWidth =
    window.innerWidth - width + Math.abs(Number.parseInt(panelX));
  document
    .getElementById("root")
    .style.setProperty("--card-stack-width", `${cardStackWidth}px`);
};

export const FilterPanel = ({
  collapsed = false,
  className = "",
  width = 300,
}) => {
  const actions = useStore(useCallback((state) => state.actions, []));
  const filters = useStore(useCallback((state) => state.filters, []));
  const dispatch = useStore(useCallback((state) => state.dispatch, []));
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  const toggleCollapsed = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCollapsed((p) => !p);
  }, []);

  useLayoutEffect(() => {
    document
      .getElementById("root")
      .style.setProperty("panel-width", `${width}px`);

    updateCSSVariables(width, Number(collapsed) ? `${width * -0.85}px` : `0px`);
  }, [collapsed, width]);

  // useLayoutEffect(() => {
  //   updateCSSVariables(
  //     width,
  //     Number(isCollapsed) ? `${width * -0.85}px` : `0px`
  //   );
  // });

  const {
    tracker: { duration: trackerWidth, start: trackerStart },
  } = useStore(useCallback((state) => state.app.timeline, []));

  const props = useSpring({
    opacity: 1,
    left: Number(isCollapsed) ? `${width * -0.85}px` : `0px`,
    config: config.default,
    onChange: ({ left }) => {
      updateCSSVariables(width, left);
      // document
      //   .getElementById("root")
      //   .style.setProperty(
      //     "--panel-x-offset",
      //     `${Math.abs(Number.parseInt(left))}px`
      //   );

      // const cardStackWidth =
      //   window.innerWidth - width + Math.abs(Number.parseInt(left));
      // document
      //   .getElementById("root")
      //   .style.setProperty("--card-stack-width", `${cardStackWidth}px`);
    },
  });

  return (
    <animated.div style={props} className={`${className} filter-panel pr2`}>
      <div className="z0 pointer flex flex-row flex-wrap col-12 items-center justify-between mb3 mt1 h1 bold pointer-events">
        <div className="mt1">
          <Button
            onClick={actions.resetAllFilters}
            className="pointer uppercase h5 block-font"
          >
            Reset All Filters
          </Button>
        </div>
        <div
          className="z0 items-center pointer pointer-events"
          style={{ marginTop: `-.5rem` }}
          onClick={toggleCollapsed}
        >
          {isCollapsed ? `→` : `←`}
        </div>
      </div>
      <div className="flex flex-row flex-wrap items-baseline justify-between col-12">
        <div className="flex flex-column items-start col-12">
          {LAYOUT.TIMELINE.FILTERS.SHOW_TIME && (
            <div>
              <div className="h6 uppercase block-font mb1">Sliding Window</div>
              <Slider
                className="mb1"
                selected={LAYOUT.TIMELINE.TRACKER.WIDTH}
                min={15}
                max={timeDay.count(
                  LAYOUT.TIMELINE.FILTERS.TIME.START,
                  LAYOUT.TIMELINE.FILTERS.TIME.END
                )}
                step="1"
                width={250}
                valueWidth={`12ch`}
                title={"Span"}
                descriptor={"days"}
                amountFormatter={(d) => formatThousands(d)}
                onChange={(val) => {
                  dispatch({
                    type: `TIMELINE_UPDATE_TRACKER`,
                    payload: {
                      duration: +val,
                      end: timeDay.offset(trackerStart, +val),
                    },
                  });
                  LAYOUT.TIMELINE.TRACKER.MASK_OBJECTS &&
                    dispatch({
                      type: "FILTER_UPDATE",
                      payload: Object.assign({}, filters, {
                        TIME: {
                          start: trackerStart,
                          end: timeDay.offset(trackerStart, +val),
                        },
                      }),
                    });
                }}
              />

              <Slider
                className="pointer-events"
                selected={LAYOUT.TIMELINE.FILTERS.TIME.START.getTime()}
                min={LAYOUT.TIMELINE.FILTERS.TIME.START.getTime()}
                max={LAYOUT.TIMELINE.FILTERS.TIME.END.getTime()}
                step="1"
                width={250}
                valueWidth={`11ch`}
                title={"Start date"}
                descriptor={""}
                toFixedAmount={0}
                amountFormatter={(d) => formatTimeDayMonthYear(d)}
                onChange={(val) => {
                  dispatch({
                    type: `TIMELINE_UPDATE_TRACKER`,
                    payload: {
                      start: new Date(+val),
                      end: timeDay.offset(val, trackerWidth),
                    },
                  });
                  LAYOUT.TIMELINE.TRACKER.MASK_OBJECTS &&
                    dispatch({
                      type: "FILTER_UPDATE",
                      payload: Object.assign({}, filters, {
                        TIME: {
                          start: new Date(+val),
                          end: timeDay.offset(val, trackerWidth),
                        },
                      }),
                    });
                }}
              />
            </div>
          )}
          <div className="block-font mb3 pointer-events">
            <div>
              <strong>Rotate</strong> the 3D visualization using left-click or
              touch (mobile) and <strong>pan</strong> using right-click drag or
              two fingers (mobile).
            </div>
            <div className="mt2">
              To search, use the 'search' feature below, or click on an icon.
            </div>
            <div className="mt2">
              Once selected, a card stack will appear listing the relevant
              details and the individuals associated with the data point, either
              per individual target within their field of operation, or viewed
              broadly according to the country plane.
            </div>
          </div>
          <Search
            className="mb3 mt2 h7"
            placeholder="Search events..."
            value={filters.TEXT_SEARCH.value}
            onChange={(val) => {
              dispatch({
                type: `FILTER_SEARCH_UPDATE`,
                payload: val,
              });
            }}
          />
          <FilterDate
            start={filters.TIME.start}
            end={filters.TIME.end}
            onChange={(val) => {
              dispatch({
                type: "FILTER_TIME_UPDATE",
                payload: val,
              });
            }}
          />
          {Object.entries(filters).map(([filterKey, o]) => {
            // only show checkbox filters
            return (
              o?.all && (
                <div className="col-12" key={`filter-group-${filterKey}`}>
                  <FilterGroup
                    className="my2"
                    filterKey={filterKey}
                    filterValues={o.all}
                    selected={o.selected}
                    groups={o.groups || null}
                    isOpen={o.isOpen || false}
                    onChange={(payload) => {
                      dispatch({
                        type: "FILTER_UPDATE",
                        payload: Object.assign({}, filters, payload),
                      });
                    }}
                    title={filterKey}
                  />
                </div>
              )
            );
          })}
        </div>
      </div>
    </animated.div>
  );
};
